import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ProxyEndPoints, LambdaEndPoints } from '../config/endpoints';
import {
  S3DestinationFolder,
  TranslationFileType,
} from '../models/s3-destination-folder';
import config from '../../../config';

const PROXY_URL = () => `${config.API_ENDPOINT}`;

/**
 * Service for interacting with S3 via the proxy backend.
 * Handles pre-signed URL generation, file uploads, and folder listing.
 */
@Injectable({ providedIn: 'root' })
export class S3FilesService {
  private readonly http = inject(HttpClient);

  /**
   * Get a pre-signed URL for uploading or downloading a file to/from S3.
   */
  getPresignedUrlForFile(
    fileName: string,
    destination: S3DestinationFolder,
    isDownload: boolean,
    jobId?: string,
    type?: TranslationFileType,
    index?: string | number,
  ): Promise<{ url: string }> {
    let params = new HttpParams()
      .set('key', fileName)
      .append('isDownload', String(isDownload))
      .append('folder', String(destination));

    if (jobId) {
      params = params.append('jobId', jobId);
    }
    if (type !== undefined) {
      params = params.append('type', String(type));
    }
    if (index !== undefined) {
      params = params.append('index', index.toString());
    }

    return lastValueFrom(
      this.http.get<{ url: string }>(
        `${PROXY_URL()}${ProxyEndPoints.preSignedUrl}`,
        { params },
      ),
    );
  }

  uploadFile(presignedUrl: string, file: File): Promise<void> {
    return lastValueFrom(
      this.http.put(presignedUrl, file, { responseType: 'text' }),
    ).then(() => undefined);
  }
  /**
   * Check if a file exists in S3.
   */
  checkIfFileExists(
    fileName: string,
    destination: S3DestinationFolder,
    jobId: string,
    type: TranslationFileType,
    index?: number,
  ): Promise<{ exists: boolean }> {
    let params = new HttpParams()
      .set('key', fileName)
      .append('folder', String(destination))
      .append('jobId', jobId)
      .append('type', String(type));

    if (index !== undefined) {
      params = params.append('index', index.toString());
    }

    return lastValueFrom(
      this.http.get<{ exists: boolean }>(
        `${PROXY_URL()}${ProxyEndPoints.checkFileExistsS3}`,
        { params },
      ),
    );
  }

  /**
   * List files in an S3 folder for a given job.
   */
  getFilesInFolder(
    folder: TranslationFileType,
    jobId: string,
  ): Promise<{ files: string[] }> {
    const params = new HttpParams()
      .set('folder', String(folder))
      .set('jobId', jobId);

    return lastValueFrom(
      this.http.get<{ files: string[] }>(
        `${PROXY_URL()}${ProxyEndPoints.getFolderFiles}`,
        { params },
      ),
    );
  }

  /**
   * Notify the backend to queue a PDF-to-DOCX conversion message for the given file.
   * The backend places the message on the translate-pdf-to-docx SQS queue.
   */
  async enqueuePdfToDocxJob(
    jobUid: string,
    fileName: string,
  ): Promise<{ status: string; messageId: string }> {
    return lastValueFrom(
      this.http.post<{ status: string; messageId: string }>(
        `${config.LAMBDA_URL}${LambdaEndPoints.sendPdfToDocxSqsMessage}`,
        { jobUid, fileName },
      ),
    );
  }

  /**
   * List files in an S3 folder for a given job, including file sizes.
   */
  getFilesInFolderWithSize(
    folder: TranslationFileType,
    jobId: string,
  ): Promise<{ files: Array<{ name: string; size: number }> }> {
    const params = new HttpParams()
      .set('folder', String(folder))
      .set('jobId', jobId)
      .set('addSize', 'true');

    return lastValueFrom(
      this.http.get<{ files: Array<{ name: string; size: number }> }>(
        `${PROXY_URL()}${ProxyEndPoints.getFolderFiles}`,
        { params },
      ),
    );
  }
}
